import * as dbAccessor from '@/utils/dbAccessor'
import { Logger, isEmptyObject, loadDataRTAsync, convertTimestampToDateInObj, deepEqual, sleep } from '@/utils/tools'
import {i18n} from '@/i18n'

const initialState = {
  version: '',
  newVersion: '',
  reloadPage: false,
  upcMap: {},
  warehouseInfo: {},
  warehousesSites: [],
  logQueue: [],
  isSysUpdating: false,
  updatingText: '',
  updatingTextCn: '',
  env: 'development',
  loaderButtonError: '',
  cronjobTimes: {},
  labelStripeKey: {},
  betaFeatures: [],
  fullBetaFeatures: [],
  labelServices: [],
  internationalLabelServices: [],
  importToUSLabelServices: [],
  isRestrictInternationalLabel: true,
  globalFunctions: {},
  labelStockTypes: [],
  labelStatisticHeaders: [],
  systemDeclaimers: {},
  square: {},
  authorize: {},
  eeveeOrgId: '',
  orgKey: '',
  apiIdList: [],
  currentExchangeRate: null,
  transactionFeeRate: 0.006,
  registrationAdvanceUsers: [],
  signupMode: 'label'
}

const state = Object.assign({}, initialState)

export const mutations = {
  reset_state (state) {
    for (let prop in state) {
      if (prop !== 'version' && prop !== 'env') {
        state[prop] = initialState[prop]
      }
    }
  },
  setVersion (state, payload) {
    console.info('set app version: ', payload)
    state.version = payload
  },
  setEnvironment (state, payload) {
    console.info('set app environment: ', payload)
    state.env = payload
  },
  setNewVersion (state, payload) {
    state.newVersion = payload
  },
  setReloadPage (state, payload) {
    console.info('set reload page flag: ', payload)
    state.reloadPage = payload
  },
  setUpcMap (state, payload) {
    state.upcMap = payload
  },
  setWarehouseInfo (state, payload) {
    state.warehouseInfo = payload
  },
  setWarehouseSites (state, payload) {
    state.warehousesSites = payload
  },
  logEvent (state, payload) {
    let newPayload = payload
    state.logQueue.push(newPayload)
  },
  clearEvents (state) {
    state.logQueue = []
  },
  setIsSysUpdating (state, payload) {
    state.isSysUpdating = payload
  },
  setIsRestrictInternationalLabel (state, payload) {
    state.isRestrictInternationalLabel = payload
  },
  setUpdatingText (state, payload) {
    state.updatingText = payload
  },
  setUpdatingTextCn (state, payload) {
    state.updatingTextCn = payload
  },
  loaderButtonError (state, payload) {
    state.loaderButtonError = payload
  },
  loaderButtonClear (state) {
    state.loaderButtonError = ''
  },
  setCronjobTimes (state, payload) {
    state.cronjobTimes = payload
  },
  setWarehouseRates (state, payload) {
    state.warehouseLimitedInfo.rates = payload
  },
  setLabelStripeKey (state, payload) {
    state.labelStripeKey = payload
  },
  setBetaFeatures (state, payload) {
    state.betaFeatures = payload
  },
  setFullBetaFeatures (state, payload) {
    state.fullBetaFeatures = payload
  },
  setLabelServices (state, payload) {
    state.labelServices = payload
  },
  setInternationalLabelServices (state, payload) {
    state.internationalLabelServices = payload
  },
  setImportToUSLabelServices (state, payload) {
    state.importToUSLabelServices = payload || []
  },
  setGlobalFunctions (state, payload) {
    for (const key in payload) {
      state.globalFunctions[key] = payload[key]
    }
  },
  setLabelStockTypes (state, payload) {
    state.labelStockTypes = payload
  },
  setLabelStatisticHeaders (state, payload) {
    state.labelStatisticHeaders = payload
  },
  setSystemDeclaimers (state, payload) {
    state.systemDeclaimers = payload
  },
  setSquare (state, payload) {
    state.square = payload
  },
  setAuthorize (state, payload) {
    state.authorize = payload
  },
  setEeveeOrgId (state, payload) {
    state.eeveeOrgId = payload
  },
  setOrgKey (state, payload) {
    state.orgKey = payload
  },
  setApiIdList (state, payload) {
    state.apiIdList = payload
  },
  setCurrentExchangeRate (state, payload) {
    state.currentExchangeRate = payload
  },
  setTransactionFeeRate (state, payload) {
    state.transactionFeeRate = payload
  },
  setRegistrationAdvanceUsers (state, payload) {
    state.registrationAdvanceUsers = payload
  },
  setSignupMode (state, payload) {
    state.signupMode = payload
  }
}

export const actions = {
  reset_state ({commit}) {
    commit('reset_state')
  },
  initializeApp ({commit, state, dispatch, getters}, payload) {
    let routineFlushLogs = () => {
      if (getters.user && state.logQueue.length > 0) {
        dbAccessor.callFunction('logUiEvents', {messages: [...state.logQueue], version: getters.version})
        commit('clearEvents')
      }
    }
    setInterval(routineFlushLogs, 15000)

    let closeEventHandler = () => {
      console.log('leaving application now')
      commit('logEvent', 'Leaving application')
      dispatch('flushEventLogs')
    }

    window.onbeforeunload = closeEventHandler
  },
  loadSystemInfo ({commit, getters, state}) {
    if (getters.subscribed['systemSetting']) return
    let successFunc = (doc) => {
      let sysAdmin
      if (doc.exists) sysAdmin = doc.data()
      let version = (sysAdmin && sysAdmin.version) || '1.1.1'
      let newVersion = version.split('.')
      let [major, minor, revision] = newVersion
      let [currentMajor, currentMinor, currentRevision] = getters.version ? getters.version.split('.') : ['1', '1', '1']
      if (parseInt(major) > parseInt(currentMajor)
          || (parseInt(major) === parseInt(currentMajor) && parseInt(minor) > parseInt(currentMinor))
          || (parseInt(major) === parseInt(currentMajor) && parseInt(minor) === parseInt(currentMinor) && parseInt(revision) > parseInt(currentRevision))) {
        console.log('reload now')
        commit('setNewVersion', version)
        commit('setReloadPage', true)
      }
      commit('setCronjobTimes', sysAdmin.cronjobTimes)
      commit('setIsSysUpdating', sysAdmin.isSysUpdating)
      commit('setIsRestrictInternationalLabel', sysAdmin.isRestrictInternationalLabel)
      commit('setUpdatingText', sysAdmin.updatingText)
      commit('setUpdatingTextCn', sysAdmin.updatingTextCn)
      commit('setLabelServices', sysAdmin.labelServices)
      commit('setLabelStockTypes', sysAdmin.labelStockTypes)
      commit('setInternationalLabelServices', sysAdmin.internationalLabelServices)
      commit('setImportToUSLabelServices', sysAdmin.importToUSLabelServices)
      Object.entries(sysAdmin.i18n).forEach(([lang, dict]) =>
        i18n.mergeLocaleMessage(lang, dict)
      )
      commit('setLabelStripeKey', {
        stripePublicLiveKey: sysAdmin.stripePublicLiveKey,
        stripePublicTestKey: sysAdmin.stripePublicTestKey
      })
      commit('setSquare', {
        squareLiveApplicationId: sysAdmin.squareLiveApplicationId,
        squareLiveVendorId: sysAdmin.squareLiveVendorId,
        squareTestApplicationId: sysAdmin.squareTestApplicationId,
        squareTestVendorId: sysAdmin.squareTestVendorId
      })
      commit('setAuthorize', {
        authorizePayLiveClientKey: sysAdmin.authorizePayLiveClientKey,
        authorizePayTestClientKey: sysAdmin.authorizePayTestClientKey,
        authorizePayLiveApiId: sysAdmin.authorizePayLiveApiId,
        authorizePayTestApiId: sysAdmin.authorizePayTestApiId
      })
      commit('setSignupMode', sysAdmin.signupMode)
      commit('setFullBetaFeatures', sysAdmin.features)
      commit('setEeveeOrgId', state.orgKey || sysAdmin.eeveeOrgId)
      commit('setApiIdList', sysAdmin.apiIdList)
      commit('setCurrentExchangeRate', sysAdmin.currentExchangeRate)
      commit('setTransactionFeeRate', sysAdmin.transactionFeeRate)
      commit('setSystemDeclaimers', sysAdmin.declaimers)
      commit('setRegistrationAdvanceUsers', sysAdmin.registrationAdvanceUsers || [])
    }

    let errorFunc = (error) => {
      console.error(`Cann't get system version`, error)
      throw error
    }

    return loadDataRTAsync(successFunc, errorFunc, (successFunc, errorFunc) => {
      let unsubscribe = dbAccessor.queryStoreRT(successFunc, errorFunc, [], 'sysAdmin', 'general')
      commit('addTosubscribed', {systemSetting: unsubscribe})
    })
  },
  // payload is ['action', 'data']
  logEvent ({commit, getters, dispatch}, payload) {
    let detail = typeof payload[1] === 'object' ? JSON.stringify(payload[1]) : payload[1]
    let message = `action(${payload[0]}), payload(${detail})`
    commit('logEvent', message)
    if (getters.eventLogs.length > 30) {
      dispatch('flushEventLogs')
    }
  },
  addChangeLog ({commit, getters}, payload) {
    const {userExtra, user} = getters
    let {uid, name = 'admin'} = userExtra || user
    payload.userInfo = {userName: name, userKey: uid}
    dbAccessor.insertStore(payload, 'changeLogs')
  },
  logCriticalEvent ({commit, dispatch}, payload) {
    let message = {
      action: payload[0],
      message: payload[1],
      isError: true
    }
    commit('logEvent', message)
    dispatch('flushEventLogs')
  },
  flushEventLogs ({commit, getters}) {
    return dbAccessor.callFunction('logUiEvents', {messages: [...state.logQueue], version: getters.version})
      .then(() => {
        commit('clearEvents')
      })
  },
  async loadUpc ({commit, getters, dispatch}) {
    commit('setLoading', true)
    if (!getters.activeWarehouse) return
    const warehouseInfo = getters.warehouseInfo || {}
    const {featureInboundForecastDropshipping = []} = warehouseInfo
    const isWarehouseHasDClassUser = !!featureInboundForecastDropshipping.length
    let productList = []
    if (isWarehouseHasDClassUser) {
      productList = await dispatch('getOrgProductsInWarehouse', {tenantKeys: getters.warehouseOrganizations.map(org => org.key)})
    }
    let successFunc = docs => {
      const warehouseInfo = getters.warehouseInfo || {}
      const {featureInboundForecastDropshipping = []} = warehouseInfo

      const handleDClassCallback = products => {
        let upcs = {}
        docs.forEach(doc => {
          let item = doc.data()
          let approvedSkus = {}
          let keywords = []
          item.approvedSkus && item.approvedSkus.forEach(sku => {
            const [organizationKey, skuForDisplay] = sku.split('_')
            approvedSkus[organizationKey] = approvedSkus[organizationKey] || []
            approvedSkus[organizationKey].push(skuForDisplay)
            keywords.push(skuForDisplay)
          })
          if (upcs[item.upc || doc.id]) console.log('dup upc: ', item.upc || doc.id)
          upcs[item.upc || doc.id] = {
            key: doc.id,
            ...item,
            approvedSkus,
            keywordForSearch: keywords.join(', ')
          }
        })
        products.forEach(product => {
          const {upc, organizationKey, sku} = product
          if (!upcs[upc]) {
            upcs[upc] = {
              key: `warehouses/${getters.activeWarehouse}/organizations/${organizationKey}/products/${product._key}`,
              ...product,
              description: product.name,
              upc,
              approvedSkus: {[organizationKey]: [sku]},
              keywordForSearch: sku.join(', ')
            }
          }
        })
        commit('setUpcMap', upcs)
        commit('setLoading', false)
      }
      const isWarehouseHasDClassUser = !!featureInboundForecastDropshipping.length
      if (isWarehouseHasDClassUser) {
        if (productList.length) {
          handleDClassCallback(productList)
          productList = []
        } else {
          dispatch('getOrgProductsInWarehouse', {tenantKeys: getters.warehouseOrganizations.map(org => org.key)})
            .then(handleDClassCallback)
        }
      } else {
        let upcs = {}
        docs.forEach(doc => {
          let item = doc.data()
          let approvedSkus = {}
          let keywords = []
          item.approvedSkus && item.approvedSkus.forEach(sku => {
            const [organizationKey, skuForDisplay] = sku.split('_')
            approvedSkus[organizationKey] = approvedSkus[organizationKey] || []
            approvedSkus[organizationKey].push(skuForDisplay)
            keywords.push(skuForDisplay)
          })
          if (upcs[item.upc || doc.id]) console.log('dup upc: ', item.upc || doc.id)
          upcs[item.upc || doc.id] = {
            key: doc.id,
            ...item,
            approvedSkus,
            keywordForSearch: keywords.join(', ')
          }
        })
        commit('setUpcMap', upcs)
        commit('setLoading', false)
      }
    }

    let errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }

    let predicates = []
    let unsubscribe = await dbAccessor.queryRT(successFunc, errorFunc, predicates, ['warehouses', getters.activeWarehouse, 'upcs'])
    await sleep(500)
    commit('addTosubscribed', {warehouseUpc: unsubscribe})
  },
  addUpc ({commit, getters}, payload) {
    commit('clearError')
    let data = {...payload}
    if (data.size) {
      if (data.size === 'custom') {
        data.inboundFee = data.inboundFee || 0
        data.outboundFee = data.outboundFee || 0
        data.storageFee = data.storageFee || 0
      } else {
        delete data.inboundFee
        delete data.outboundFee
        delete data.storageFee
      }
    }
    data.location = data.location || []
    if (data.size === undefined) {
      data.size = null
    }
    return dbAccessor.insertStore(data, 'warehouses', getters.activeWarehouse, 'upcs')
      .catch((error) => {
        console.log(error)
        commit('setError', error)
      })
  },
  async deleteUpc ({commit, getters}, payload) {
    commit('clearError')
    const docs = await dbAccessor.queryStore('warehouses', getters.activeWarehouse, 'inventory')
    const hasInventory = docs.docs.some((doc) => {
      const {distribution = {}, abnormalDistribution = {}} = doc.data()
      return !!distribution[payload.upc] || !!abnormalDistribution[payload.upc]
    })
    const hasOutbound = getters.shipments.some(shipment => {
      return shipment.products.some(product => product.upc === payload.upc)
    })
    if (hasInventory) {
      throw Error('This upc has inventory, please clean before deleting.')
    }
    if (hasOutbound) {
      throw Error('This upc has outbound, please clean before deleting.')
    }
    let tenantKeys = []
    if (payload.approvedSkus) {
      tenantKeys = Object.keys(payload.approvedSkus)
    }
    let tenantInventoryDocsArray = []
    if (tenantKeys.length !== 0) {
      const predicates = [{ field: 'upc', compare: '==', value: payload.upc }]
      tenantInventoryDocsArray = await Promise.all(tenantKeys.map(tenantKey => {
        return dbAccessor.queryWithPredicatesStore(predicates, 'tenants', tenantKey, 'inventory')
      }))
    }

    dbAccessor.runInTransactionStore(async transaction => {
      let warehouseUpcsDoc = await transaction.get(dbAccessor.buildStoreQuery(['warehouses',
        getters.activeWarehouse, 'upcs', payload.key]))

      if (tenantInventoryDocsArray.length !== 0) {
        const tenantInventoryRefs = tenantInventoryDocsArray.map(docs => docs.docs.map(doc => doc.ref))
        const tenantInventoryDocs = await Promise.all(
          tenantInventoryRefs.map(docs => transaction.get(...docs))
        )
        tenantInventoryDocs.forEach(doc => {
          const filteredTenantProductApprovedSkus = doc.data().approvedSkus.filter(item =>
            item.split('_')[0] !== getters.activeWarehouse)
          transaction.update(doc.ref,
            dbAccessor.addUpdateDocTimestamp({approvedSkus: filteredTenantProductApprovedSkus}))
        })
      }

      transaction.delete(warehouseUpcsDoc.ref)
    })
  },

  async updateUpc ({commit, getters}, payload) {
    commit('clearError')
    let {description, key, size, inboundFee, outboundFee, storageFee, location = [], upc, siteKey, snTemplate = ''} = payload
    let originLocation = (getters.upcMap[upc] && getters.upcMap[upc].location) || []
    let updateFields = {}
    let newLocation = []

    updateFields.snTemplate = snTemplate
    description && (updateFields.description = description)
    if (!!size || size === 0) {
      updateFields.size = size
      if (size === 'custom') {
        updateFields.inboundFee = inboundFee || 0
        updateFields.outboundFee = outboundFee || 0
        updateFields.storageFee = storageFee || 0
      } else {
        updateFields.inboundFee = dbAccessor.deleteField()
        updateFields.outboundFee = dbAccessor.deleteField()
        updateFields.storageFee = dbAccessor.deleteField()
      }
    }
    if (siteKey) {
      newLocation = [...originLocation.filter(item => item.siteKey !== siteKey), {
        siteKey,
        location
      }]
    }
    updateFields.location = newLocation
    const keys = key.split('/')
    const isisDClassProduct = keys.length === 6
    let promise = null
    if (isisDClassProduct) {
      promise = dbAccessor.runInTransactionStore(async transaction => {
        const warehouseDoc = dbAccessor.buildStoreQuery(keys)
        const warehouseProductDoc = await transaction.get(warehouseDoc)
        if (warehouseProductDoc.exists) {
          transaction.update(warehouseProductDoc.ref, dbAccessor.addUpdateDocTimestamp(updateFields))
          const upcMap = getters.upcMap
          const upcItem = upcMap[upc]
          upcMap[upc] = {
            ...upcItem,
            ...updateFields
          }
          commit('setUpcMap', upcMap)
        }
      })
    } else {
      promise = dbAccessor.updateFieldsStore(updateFields, 'warehouses', getters.activeWarehouse, 'upcs', key)
    }
    return promise
      .catch((error) => {
        console.log(error)
      })
  },
  async loadAllWarehouses ({commit, getters, dispatch}) {
    if (!getters.user.isAdmin) return
    commit('setWarehouses', await dispatch('getAllWarehouses', true))
  },
  async loadWarehouseInfo ({commit, getters}) {
    if (!getters.activeWarehouse) {
      return
    }
    let successFunc = (doc) => {
      if (doc.exists) {
        let warehouseInfo = doc.data()
        if (getters.warehouseInfo) {
          let oldWarehouseInfo = getters.warehouseInfo
          const {featureInboundForecastPkg = [], featureInboundForecast = [], featureInboundForecastDropshipping = []} = oldWarehouseInfo
          commit('setWarehouseInfo', {...warehouseInfo, featureInboundForecastPkg, featureInboundForecast, featureInboundForecastDropshipping})
        } else {
          commit('setWarehouseInfo', warehouseInfo)
        }
      }
    }

    let errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }
    return loadDataRTAsync(successFunc, errorFunc, (successFunc, errorFunc) => {
      let unsubscribe = dbAccessor.queryRT(successFunc, errorFunc, [], ['warehouses', getters.activeWarehouse])
      commit('addTosubscribed', {warehouseInfo: unsubscribe})
    })
  },
  loadWarehouseSite ({commit, getters}, payload) {
    commit('setLoading', true)
    if (!getters.activeWarehouse) {
      return
    }
    let successFunc = docs => {
      const warehousesStore = []
      docs.forEach(doc => {
        let site = doc.data()
        site.key = doc.id
        warehousesStore.push(site)
      })
      commit('setWarehouseSites', warehousesStore)
      commit('setLoading', false)
    }

    let errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }

    let predicates = []
    let unsubscribe = dbAccessor.queryRT(successFunc, errorFunc, predicates, ['warehouses', getters.activeWarehouse, 'sites'])
    commit('addTosubscribed', {warehouseSites: unsubscribe})
  },
  clearAllWarehousesData ({getters, state, commit}) {
    getters.subscribed.warehouseSites && getters.subscribed.warehouseSites()
    let data = 'warehouseSites'
    commit('clearSubscribed', {data, initValues: initialState[data]})
  },
  addWarehouseSite ({commit, getters}, payload) {
    commit('clearError')
    dbAccessor.insertStore(payload, 'warehouses', getters.activeWarehouse, 'sites')
      .then((data) => {
        const key = data.id
        commit('addWarehouseSites', {
          ...payload,
          key: key
        })
      })
      .catch((error) => {
        console.log(error)
        commit('setError', error)
      })
  },
  addTenant ({commit, getters}, payload) {
    commit('clearError')
    return dbAccessor.insertStore(payload, 'tenants')
      .then((data) => {
        const id = data.id
        commit('addTenant', {
          ...payload,
          id: id
        })
        return id
      })
      .catch((error) => {
        console.log(error)
        commit('setError', error)
      })
  },
  editAdminFeature ({commit, getters}, payload) {
    const historyOrgId = getters.eeveeOrgId
    const apiIdList = getters.apiIdList || []
    const {features = [], eeveeOrgId = ''} = payload
    const isNeedToUpdateSysOrgId = historyOrgId === eeveeOrgId
    const needToUpdateFeatures = getters.fullBetaFeatures.map(feature => ({
      ...feature,
      isEnabled: features.includes(feature.key)
    }))
    const update = isNeedToUpdateSysOrgId ? {
      'features': needToUpdateFeatures
    } : {
      'features': needToUpdateFeatures,
      eeveeOrgId,
      apiIdList: [...apiIdList.filter(apiId => apiId && apiId !== historyOrgId), eeveeOrgId]
    }
    return dbAccessor.updateFieldsStore(update, 'sysAdmin', 'general')
  },
  async editClientConfig ({commit, getters}, payload) { // margin值为百分比分数分子，用来做计算时，需要除以100
    let {clientKey, discount = {}, accountManager = '',
      lowestBalance = 0, features, clientName, companyName = '', note = '',
      bond = 0, type, isOverseaCustomer, monthlyFee = 0,
      freightSites = [], isBlockCreditCardDeposit = false,
      depositFeePercent = 0, isDisabledOutbound = false, margin = 0,
      eeveeOrgId = '', inVisibleFeatures = [], isChangedApiId = false, weChatGroupId = '', labelMapping = false, department = '', tags = [], contractSigning = false, contractSigningValidDate = []} = payload
    if (note.length > 10000) note = note.subString(0, 10000)

    await dbAccessor.runInTransactionStore(async transaction => {
      let balanceDoc = await transaction.get(dbAccessor.buildStoreQuery(['systemBalance', clientKey]))
      let updates = {
        discount,
        accountManager,
        clientName,
        companyName,
        lowestBalance,
        note,
        bond,
        isOverseaCustomer,
        monthlyFee: +monthlyFee,
        margin: +margin,
        isBlockCreditCardDeposit,
        department,
        tags,
        weChatGroupId,
        labelMapping,
        contractSigning,
        contractSignStartDate: contractSigning ? contractSigningValidDate[0] : '',
        contractSignEndDate: contractSigning ? contractSigningValidDate[1] : ''
      }
      const eeveeTenantKeyMap = getters.eeveeIdMatchTenantKeyMap
      if (eeveeTenantKeyMap.has(eeveeOrgId) && eeveeTenantKeyMap.get(eeveeOrgId) !== clientKey) throw Error('eeveeId existed')
      // 将新关联的apiId维护到一个list里面
      if (isChangedApiId) {
        if (eeveeOrgId) {
          updates.eeveeOrgId = eeveeOrgId.trim()
          const systemAdminGeneralDoc = await transaction.get(dbAccessor.buildStoreQuery(['sysAdmin', 'general']))
          const { apiIdList = [] } = systemAdminGeneralDoc.data()
          transaction.update(systemAdminGeneralDoc.ref, {apiIdList: [...new Set([...apiIdList, eeveeOrgId])]})
        } else {
          updates.eeveeOrgId = dbAccessor.deleteField()
        }
      }
      if (type === 'warehouse') {
        updates.isDisabledOutbound = isDisabledOutbound
        updates.depositFeePercent = +depositFeePercent
      }
      // 如果features中包含托盘服务，则将拖盘相关数据更新进数据库
      // 如果是曾经使用过，然后关闭，再重新打开会使用以往配置
      if (features?.includes('FEDEX_LTL')) {
        updates.freightSites = freightSites.includes('ALL') ? ['ALL'] : freightSites
      }
      if (balanceDoc.exists) {
        transaction.update(balanceDoc.ref, updates)
        const beforeValue = getters.systemBalances.find(item => item.clientKey === clientKey) || {}
        if (!deepEqual(updates, beforeValue, {isSubset: true, exceptKey: 'note'})) {
          delete updates.eeveeOrgId
          Logger.changeLog({
            beforeValue: beforeValue,
            actionPayload: {...updates},
            actionName: 'editClientConfig',
            categories: ['config'],
            keys: [clientKey],
            keywords: [clientName]
          })
        }
      } else {
        transaction.set(balanceDoc.ref, {...updates, balance: 0})
      }
      const typesMap = {
        organization: 'tenantLimitedInfo',
        warehouse: 'warehouseLimitedInfo'
      }
      const updateType = typesMap[type]
      if (!updateType) return
      transaction.update(dbAccessor.buildStoreQuery([updateType, clientKey]), {
        betaFeatures: features,
        inVisibleFeatures: inVisibleFeatures
      })
    })
  },
  bindTenantWithApiId ({commit, getters}, payload) {
    const {eeveeOrgId = '', clientKey = '', clientName = ''} = payload
    return dbAccessor.callFunction('bindTenantWithApiId', {eeveeOrgId, clientKey, clientName})
  },
  updateOrganization ({commit, getters}, payload) {
    commit('clearError')
    const beforeValue = getters.tenantsWithBillings.find(item => item.clientKey === payload.clientKey) || {}

    payload.warehouseKey = getters.activeWarehouse
    payload.warehouseName = getters.warehouseName
    payload.sites = getters.warehousesSites
    let discount = getters.tierDiscounts.find(item => item.key === payload.discountKey)
    if (discount) {
      payload.discountRate = discount.discountRate
    } else {
      payload.discountRate = 0
    }

    let {requestId, balance, expenseHistory, createTime, lastModifiedTime, waives, key, rates, ...org} = payload
    org.id || (org.id = key)
    org.organizationId && (org.organizationId = org.organizationId.trim())
    const set = new Set(waives)
    org.isInboundWaived = set.has('inbound')
    org.isOutboundWaived = set.has('outbound')
    org.isStorageWaived = set.has('storage')
    org.discountKey || (org.discountKey = null)
    return dbAccessor.callFunction('updateOrganization', org)
      .then(() => {
        requestId && dbAccessor.removeStore('joinWarehouseRequests', requestId)
        if (!deepEqual(beforeValue, payload, {isSubset: true, exceptKey: 'note'})) {
          Logger.changeLog({
            beforeValue: beforeValue,
            actionPayload: {...org},
            actionName: 'editOrganization',
            categories: ['organization'],
            keys: [org.warehouseKey],
            keywords: [org.warehouseKey, org.clientKey]
          })
        }
      })
  },
  updateLabelStatus ({getters}) {
    return getters.user.isAdmin && dbAccessor.callFunction('updateLabelsStatus', {})
      .then((data) => {
        alert('label update function has updated ' + data.data + ' labels')
      })
  },
  exportOP950Document ({getters}, payload) {
    const isAdmin = getters.user && getters.user.isAdmin
    const operator = getters.operator
    if (!(isAdmin || getters.activeWarehouse)) return
    return dbAccessor.callFunction('processShippingLabel', {
      ...payload,
      operatorName: operator.name || 'admin',
      operatorEmail: operator.email,
      type: 'OP950'
    })
  },
  generateLabelInvoice ({ getters }, payload) {
    const hasPermission = getters.betaFeatures?.includes('exportClientTransactions')
    if (getters.user.isAdmin || hasPermission) {
      return dbAccessor.callFunction('generateLabelInvoice', payload)
    }
  },
  searchSystemTransactions ({getters}, payload) {
    let {value, field} = payload
    let predicates = [{
      field: field,
      compare: '==',
      value: value
    }]
    return dbAccessor.queryWithPagination(predicates, ['systemTransactions'], null, false, null, payload.limit)
      .then((docs) => {
        let transactions = []
        docs.forEach(doc => {
          if (doc.exists) {
            const obj = convertTimestampToDateInObj(doc.data())
            transactions.push((obj))
          }
        })
        return transactions
      })
  },
  async getClientsDayEndingClosings (store, payload = {}) {
    const {date = new Date(Date.now() - 86400000).toISOString().slice(0, 10)} = payload

    const dayEndClosingsDoc = await dbAccessor.queryStore('sysAdmin', 'general', 'dayEndClosings', date)
    if (!dayEndClosingsDoc.exists) return []
    const { clientsDayEndClosings = [] } = dayEndClosingsDoc.data()
    return clientsDayEndClosings.map(item => convertTimestampToDateInObj(item))
  }
}

export const getters = {
  version (state) {
    return state.version
  },
  newVersion (state) {
    return state.newVersion
  },
  env (state) {
    return state.env
  },
  reloadPage (state) {
    return state.reloadPage
  },
  tempSkus (state) {
    return Object.values(state.upcMap).filter((item) => item.isSku)
  },
  tempSkuMap (state, getters) {
    let map = {}
    getters.tempSkus.forEach(item => {
      const {organizationKey, sku} = item
      map[`${organizationKey}_${sku}`] = item
    })
    return map
  },
  upcMap (state, getters) {
    Object.values(state.upcMap).forEach(item => {
      if (!item.location) item.location = []
      getters.warehouseSites.forEach(site => {
        let target = item.location.find(loc => loc.siteKey === site.key)
        if (!target) {
          item.location.push({
            siteKey: site.key,
            location: []
          })
        }
      })
    })
    return state.upcMap
  },
  skuToUpcMap (state) {
    let map = {}
    Object.entries(state.upcMap).forEach(([upc, value]) => {
      const {approvedSkus = {}} = value
      Object.entries(approvedSkus).forEach(([organizationKey, skus]) => {
        skus.forEach(sku => {
          map[`${organizationKey}_${sku}`] = upc
        })
      })
    })
    return map
  },
  getUpcName (state) {
    return key => state.upcMap[key] && state.upcMap[key].description
  },
  currentWarehouseUserSites ({ warehouseInfo }, { user, mainAccount }) {
    const { uid } = user || {}
    if (mainAccount) {
      return (warehouseInfo.users || []).find(staff => staff.email === mainAccount.email)?.sites || []
    }
    const { sites = [] } = (warehouseInfo.users || []).find(staff => uid === staff.key) || {}
    return sites
  },
  warehouseInfo (state) {
    return state.warehouseInfo
  },
  warehousesSites ({ warehousesSites }, { activeWarehouse, user, currentWarehouseUserSites }) {
    const { roles = [] } = user || {}
    return activeWarehouse && !roles.includes('warehouseOwner')
      ? currentWarehouseUserSites.includes('all')
        ? warehousesSites
        : warehousesSites.filter(site => currentWarehouseUserSites.includes(site.key))
      : warehousesSites
  },
  warehousesSiteKeyMap (state) {
    return new Map(state.warehousesSites.map(item => [item.key, item]))
  },
  eventLogs (state) {
    return state.logQueue
  },
  isSysUpdating (state) {
    return state.isSysUpdating
  },
  isRestrictInternationalLabel (state) {
    return state.isRestrictInternationalLabel
  },
  updatingText (state) {
    return state.updatingText
  },
  updatingTextCn (state) {
    return state.updatingTextCn
  },
  loaderButtonError (state) {
    return state.loaderButtonError
  },
  upcSizes (state, getters) {
    return [...getters.warehouseLimitedInfo.rates.unitRates, { name: 'custom' }]
  },
  cronjobTimes (state) {
    return state.cronjobTimes
  },
  labelStripeKey (state) {
    return state.labelStripeKey
  },
  limitedInfo (state, getters) {
    const { tenantLimitedInfo, warehouseLimitedInfo } = getters
    const limitInfo = isEmptyObject(tenantLimitedInfo)
      ? isEmptyObject(warehouseLimitedInfo)
        ? {}
        : warehouseLimitedInfo
      : tenantLimitedInfo
    return limitInfo
  },
  betaFeatures (state, getters) {
    const { limitedInfo, fullBetaFeatures } = getters

    const betaFeatures = limitedInfo.betaFeatures || []
    return betaFeatures.filter(betaFeature => {
      const feature = (fullBetaFeatures || []).find(sysFeature => sysFeature.key === betaFeature)
      return feature && feature.isEnabled
    })
  },
  visibleBetaFeatures (state, getters) {
    const { betaFeatures, fullBetaFeatures, limitedInfo } = getters
    const inVisibleFeatures = limitedInfo.inVisibleFeatures || []

    let visibleBetaFeatures = new Set(betaFeatures)
    fullBetaFeatures.forEach(feature => {
      feature.isVisible && visibleBetaFeatures.add(feature.key)
    })
    inVisibleFeatures.forEach(key => {
      visibleBetaFeatures.has(key) && visibleBetaFeatures.delete(key)
    })
    return [...visibleBetaFeatures]
  },
  fullBetaFeatures (state) {
    return state.fullBetaFeatures
  },
  eeveeOrgId (state) {
    return state.eeveeOrgId
  },
  apiIdList (state) {
    return state.apiIdList
  },
  warehouseBetaFeatures (state) {
    return state.warehouseBetaFeatures
  },
  labelServices (state, getters) {
    return state.labelServices.map(item => ({
      ...item,
      serviceTypes: item.serviceTypes.filter(ls => getters.visibleBetaFeatures.includes(ls.value))
    }))
  },
  allLabelServices (state, getters) {
    return state.labelServices.map(item => ({
      ...item,
      serviceTypes: item.serviceTypes
    }))
  },
  labelStockTypes (state) {
    return state.labelStockTypes
  },
  shippingServices (state, getters) {
    const expandedServices = getters.labelServices.filter(service => !service.isFreight)
      .reduce((services, {signatureOptions, serviceTypes = [], ...settings}, idx) => [
        ...services,
        ...serviceTypes.map(service => ({...settings, ...service})),
        { divider: true }
      ], [])

    const recommendedServices = expandedServices.filter(service => service.sortIndex > 0).sort((a, b) => a.sortIndex - b.sortIndex)

    return [].concat(
      recommendedServices,
      [{ divider: true }],
      expandedServices
    )
  },
  freightShippingServices (state, getters) {
    const expandedServices = getters.labelServices.filter(service => service.isFreight)
      .reduce((services, {signatureOptions, serviceTypes = [], ...settings}, idx) => [
        ...services,
        ...serviceTypes.map(service => ({...settings, ...service})),
        { divider: true }
      ], [])

    const recommendedServices = expandedServices.filter(service => service.sortIndex > 0).sort((a, b) => a.sortIndex - b.sortIndex)

    return [].concat(
      recommendedServices,
      [{ divider: true }],
      expandedServices
    )
  },
  internationalLabelServices (state, getters) {
    return state.internationalLabelServices.map(item => ({
      ...item,
      serviceTypes: item.serviceTypes.filter(ls => getters.visibleBetaFeatures.includes(ls.value))
    }))
  },
  importToUSLabelServices (state, getters) {
    return state.importToUSLabelServices.map(item => ({
      ...item,
      serviceTypes: item.serviceTypes.filter(ls => getters.visibleBetaFeatures.includes(ls.value))
    }))
  },
  labelDefaultService (state) {
    for (let labelService of state.labelServices) {
      const {serviceTypes = []} = labelService
      let defaultVal = serviceTypes.find((serviceType) => serviceType.default)
      if (defaultVal) {
        return defaultVal.value
      }
    }
    return 'FEDEX_GROUND'
  },
  labelDefaultCarrier (state, getters) {
    return state.labelServices.find(({serviceTypes = [ ]}) =>
      serviceTypes.some(({value}) =>
        value === getters.labelDefaultService
      )
    ).carrier || 'FedEx'
  },
  getCarrierByServiceType (state) {
    return (serviceType) => {
      const vendor = state.labelServices.find(vendor => vendor.serviceTypes.some(({value}) => serviceType === value))
      if (vendor) return vendor.carrier
      return false
    }
  },
  allLabelSeverTypeSettingsMap (state, getters) {
    let map = {}
    for (const labelService of [...state.labelServices, ...state.internationalLabelServices, ...state.importToUSLabelServices]) {
      const {
        enableMWT: labelServiceEnableMWT = false,
        enableInsured: labelServiceEnableInsured = false,
        disabledPkgMemo: labelServiceDisabledPkgMemo = false,
        memoLength: labelServiceMemoLength = 40,
        maxPkgQty: labelServiceMaxPkgQty = 200,
        cancelHint: labelServiceCancelHint = '',
        allowedFromStates: labelServiceAllowedFromStates = [],
        isFreight: labelServiceIsFreight = false,
        carrier = 'FEDEX',
        serviceTypes = [],
        // betaFeature: labelBetaFeature = '',
        signatureOptions: labelServiceSignatureOptions = []
      } = labelService
      for (const serviceType of serviceTypes) {
        const {
          enableMWT = labelServiceEnableMWT,
          enableInsured = labelServiceEnableInsured,
          disabledPkgMemo = labelServiceDisabledPkgMemo,
          memoLength = labelServiceMemoLength,
          maxPkgQty = labelServiceMaxPkgQty,
          cancelHint = labelServiceCancelHint,
          allowedFromStates = labelServiceAllowedFromStates,
          // betaFeature = labelBetaFeature,
          signatureOptions = labelServiceSignatureOptions,
          isFreight = labelServiceIsFreight,
          value = '',
          text = ''
        } = serviceType
        map[`${carrier}_${value}`.toUpperCase()] = {
          enableMWT,
          enableInsured,
          disabledPkgMemo,
          memoLength,
          maxPkgQty,
          cancelHint,
          allowedFromStates,
          signatureOptions,
          betaFeature: text,
          isFreight
        }
      }
    }
    return map
  },
  getLabelServiceSettingByCarrierAndServiceTypeValue (state, getters) {
    return (carrier, serviceTypeValue) =>
      (
        getters.allLabelSeverTypeSettingsMap[`${carrier}_${serviceTypeValue}`.toUpperCase()] ||
        { enableMWT: false, enableInsured: false, disabledPkgMemo: false }
      )
  },
  globalFunctions (state, getters) {
    return state.globalFunctions
  },
  labelStatisticHeaders (state) {
    return state.labelStatisticHeaders
  },
  systemDeclaimers (state) {
    return state.systemDeclaimers
  },
  square (state) {
    return state.square
  },
  authorize (state) {
    return state.authorize
  },
  currentExchangeRate (state) {
    return state.currentExchangeRate
  },
  transactionFeeRate (state) {
    return state.transactionFeeRate
  },
  isNonVPN (state) {
    const isMirrorImage = location.host === process.env.nonVPNDomain
    return navigator.language.includes('zh') && isMirrorImage
  },
  registrationAdvanceUsers (state) {
    return state.registrationAdvanceUsers || []
  },
  signupMode (state) {
    return state.signupMode || 'label'
  }
}

export default {
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/setting/index.js